import React, { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { UploadCloud, Film, Images, ScanLine, Sparkles, Wallet, CheckCircle2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";

const STAGES = [
  { key: "detecting_cards", label: "Detecting each card in the stack", icon: ScanLine },
  { key: "identifying_and_pricing", label: "Identifying cards & pulling last-sold prices", icon: Sparkles },
  { key: "done", label: "Binder ready", icon: Wallet },
];

const MAX_FILE_SIZE_MB = 500;
const MAX_PHOTOS = 40;
const MAX_PHOTO_SIZE_MB = 20;

export default function Upload() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [mode, setMode] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const resetUploader = () => {
    setJobId(null);
    setJob(null);
    setFiles([]);
    setMode(null);
    setUploading(false);
    setUploadProgress(0);
    setConfirmCancel(false);
  };

  const cancelScan = async () => {
    if (!confirmCancel) {
      setConfirmCancel(true);
      setTimeout(() => setConfirmCancel(false), 4000);
      return;
    }
    try {
      await api.post(`/videos/${jobId}/cancel`);
      clearInterval(pollRef.current);
      resetUploader();
      checkAuth();
      toast.success("Scan cancelled — you can start a fresh one");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't cancel the scan");
    }
  };

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    if (arr.length === 1 && arr[0].type.startsWith("video/")) {
      if (arr[0].size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error(`Video is too large (max ${MAX_FILE_SIZE_MB}MB). Try trimming the clip.`);
        return;
      }
      setFiles(arr);
      setMode("video");
      return;
    }
    if (arr.every((f) => f.type.startsWith("image/"))) {
      if (arr.length > MAX_PHOTOS) {
        toast.error(`Max ${MAX_PHOTOS} photos per scan`);
        return;
      }
      if (arr.some((f) => f.size > MAX_PHOTO_SIZE_MB * 1024 * 1024)) {
        toast.error(`Each photo must be under ${MAX_PHOTO_SIZE_MB}MB`);
        return;
      }
      setFiles(arr);
      setMode("photos");
      return;
    }
    toast.error("Upload one stacking video, or a set of card photos (JPG/PNG/WEBP)");
  };

  const startPolling = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/videos/${id}`);
        setJob(res.data);
        // As soon as the first card lands, hand off to the live Review page - the scan
        // keeps running in the background and cards roll in as they finish.
        if (res.data.status === "processing" && (res.data.card_ids?.length || 0) > 0) {
          clearInterval(pollRef.current);
          navigate(`/review/${id}`);
          return;
        }
        if (res.data.status === "cancelled") {
          clearInterval(pollRef.current);
          checkAuth();
          resetUploader();
          toast.info("Scan cancelled");
          return;
        }
        if (res.data.status === "completed" || res.data.status === "failed") {
          clearInterval(pollRef.current);
          checkAuth();
          if (res.data.status === "completed") {
            setTimeout(() => navigate(`/review/${id}`), 800);
          }
        }
      } catch {
        clearInterval(pollRef.current);
      }
    }, 2000);
  };

  const CHUNK_SIZE = 4 * 1024 * 1024;

  const uploadVideo = async (file) => {
    // Chunked upload: the server proxy caps single-request sizes, so the video is sent
    // as small 4MB pieces that any proxy allows, then reassembled server-side.
    const init = await api.post("/videos/upload/init", { filename: file.name });
    const id = init.data.id;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < totalChunks; i++) {
      const offset = i * CHUNK_SIZE;
      const blob = file.slice(offset, Math.min(offset + CHUNK_SIZE, file.size));
      const fd = new FormData();
      fd.append("chunk", blob, "chunk.bin");
      fd.append("chunk_offset", String(offset));
      let attempts = 0;
      for (;;) {
        try {
          await api.post(`/videos/upload/${id}/chunk`, fd, { timeout: 0 });
          break;
        } catch (err) {
          attempts += 1;
          if (attempts >= 3) throw err;
          await new Promise((r) => setTimeout(r, 1500));
        }
      }
      setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
    }
    return api.post(`/videos/upload/${id}/complete`, { total_size: file.size });
  };

  const uploadPhotos = async () => {
    // One image per request keeps every request small enough for any proxy.
    const init = await api.post("/photos/upload/init");
    const id = init.data.id;
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append("file", files[i]);
      let attempts = 0;
      for (;;) {
        try {
          await api.post(`/photos/upload/${id}/image`, fd, { timeout: 0 });
          break;
        } catch (err) {
          if (err.response?.status === 400 || err.response?.status === 413) throw err;
          attempts += 1;
          if (attempts >= 3) throw err;
          await new Promise((r) => setTimeout(r, 1500));
        }
      }
      setUploadProgress(Math.round(((i + 1) / files.length) * 100));
    }
    return api.post(`/photos/upload/${id}/complete`);
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const done = mode === "photos" ? await uploadPhotos() : await uploadVideo(files[0]);
      setJobId(done.data.id);
      setJob(done.data);
      startPolling(done.data.id);
    } catch (e) {
      console.error("Upload failed", e);
      let message = "Upload failed. Please try again.";
      if (e.code === "ERR_NETWORK") {
        message = "Upload failed: connection was interrupted. Check your network and try again.";
      } else if (e.response?.status === 413) {
        message = e.response?.data?.detail || "Upload failed: file exceeds the size limit.";
      } else if (e.response?.status === 402) {
        message = e.response?.data?.detail || "You're out of credits.";
      } else if (e.response?.data?.detail) {
        message = `Upload failed: ${e.response.data.detail}`;
      } else if (e.message) {
        message = `Upload failed: ${e.message}`;
      }
      toast.error(message);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const stages = job?.job_type === "photos" ? STAGES.filter((s) => s.key !== "detecting_cards") : STAGES;
  const currentStageIndex = job ? stages.findIndex((s) => s.key === job.step) : -1;

  return (
    <div className="min-h-screen bg-[#05060f] text-white px-6 py-16">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-display text-3xl md:text-4xl font-bold mb-2">Scan your stack</h1>
        <p className="text-zinc-400 mb-4 text-sm md:text-base">Record yourself placing cards one on top of another, or slowly flipping through your binder pausing on each card — or upload individual photos of each card.</p>

        <Link
          to="/import"
          data-testid="upload-bulk-import-link"
          className="inline-flex items-center gap-1.5 mb-6 text-xs text-[#A78BFA] hover:text-white transition-colors font-medium border border-[#A78BFA]/30 hover:border-[#A78BFA]/60 bg-[#A78BFA]/5 rounded-full px-3 py-1.5"
        >
          Already have a spreadsheet of cards? Bulk import CSV / Excel →
        </Link>

        {user && (
          <div data-testid="upload-credits-notice" className="flex items-center gap-2 text-xs text-zinc-500 mb-8">
            <Zap className="w-3.5 h-3.5 text-[#A78BFA]" /> {user.credits_balance} credit{user.credits_balance === 1 ? "" : "s"} available (0.25 credits per card) ·{" "}
            <Link to="/billing" className="text-[#A78BFA] hover:underline">manage billing</Link>
          </div>
        )}

        <div data-testid="upload-pokemon-notice" className="border border-white/10 bg-[#111113] rounded-xl p-4 mb-6 text-xs text-zinc-400 leading-relaxed">
          <span className="text-[#A78BFA] font-semibold">Works best with Pokemon cards.</span> Sports cards can be
          added too — just expect less accurate pricing and occasional imperfect image matches, since sports market
          data is thinner. First scan?{" "}
          <Link to="/guides/scanning-best-practices" data-testid="upload-scanning-guide-link" className="text-[#A78BFA] underline underline-offset-2 hover:text-white transition-colors">
            Read the scanning guide
          </Link>{" "}
          for the best results.
          <span data-testid="upload-duplicate-warning" className="block mt-2 pt-2 border-t border-white/10">
            <span className="text-[#F59E0B] font-semibold">One of each card per video:</span> don't scan two identical
            cards in the same run — the AI treats the second as a double-trigger and skips it. Duplicates you already
            own stack onto your existing card automatically, or set the copy count from the card's page.
          </span>
        </div>

        {user?.credits_balance <= 0 && (
          <div data-testid="upload-no-credits-banner" className="border border-[#F59E0B]/30 bg-[#F59E0B]/5 rounded-xl p-5 mb-6 text-sm text-[#F59E0B] flex items-center justify-between flex-wrap gap-3">
            <span>You're out of credits, so new scans can't be identified or priced right now.</span>
            <Link to="/billing"><Button size="sm" className="bg-[#F59E0B] text-black hover:bg-[#d97f06] rounded-full">Buy credits</Button></Link>
          </div>
        )}

        {!jobId && (
          <div
            data-testid="video-uploader-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            onClick={() => !uploading && inputRef.current?.click()}
            className={`border-2 border-dashed border-white/15 hover:border-[#A78BFA]/50 rounded-2xl p-12 flex flex-col items-center justify-center text-center transition-colors bg-[#111113] ${uploading ? "cursor-default" : "cursor-pointer"}`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="video/*,image/*"
              multiple
              data-testid="video-uploader-input"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            {files.length ? (
              mode === "photos" ? <Images className="w-10 h-10 text-[#A78BFA] mb-4" /> : <Film className="w-10 h-10 text-[#A78BFA] mb-4" />
            ) : (
              <UploadCloud className="w-10 h-10 text-zinc-500 mb-4" />
            )}
            <p className="font-medium text-white mb-1" data-testid="video-uploader-filename">
              {files.length
                ? mode === "photos"
                  ? `${files.length} card photo${files.length === 1 ? "" : "s"} selected`
                  : files[0].name
                : "Drop your stacking or binder flip-through video — or individual card photos"}
            </p>
            <p className="text-xs text-zinc-500">MP4/MOV/WEBM up to {MAX_FILE_SIZE_MB}MB, or up to {MAX_PHOTOS} JPG/PNG/WEBP photos (one card per photo) — click to browse</p>
          </div>
        )}

        {uploading && (
          <div data-testid="video-upload-progress" className="mt-6 space-y-2">
            <div className="flex items-center justify-between text-xs text-zinc-400">
              <span>{mode === "photos" ? "Uploading photos..." : "Uploading video..."}</span>
              <span data-testid="video-upload-progress-percent" className="font-mono-plex text-[#A78BFA]">{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="bg-white/10" />
          </div>
        )}

        {!jobId && (
          <Button
            data-testid="video-uploader-submit-button"
            disabled={!files.length || uploading || user?.credits_balance <= 0}
            onClick={handleUpload}
            className="mt-6 w-full bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full py-6 text-base font-semibold disabled:opacity-40 transition-colors"
          >
            {uploading ? `Uploading... ${uploadProgress}%` : "Start AI Scan"}
          </Button>
        )}

        {jobId && (
          <div data-testid="video-processing-status" className="mt-4 space-y-4">
            {job?.status === "processing" && (
              <p className="text-[10px] text-zinc-600">You can leave this page — scanning continues in the background and finished cards land in your binder.</p>
            )}
            {job?.status === "failed" && (
              <div data-testid="video-processing-error" className="border border-red-500/30 bg-red-500/5 rounded-xl p-6 text-red-400 text-sm">
                {job.error_message || "Processing failed. Please try a clearer video."}
              </div>
            )}
            {stages.map((stage, i) => {
              const active = i === currentStageIndex;
              const done = job?.status === "completed" || i < currentStageIndex;
              return (
                <div
                  key={stage.key}
                  data-testid={`processing-stage-${stage.key}`}
                  className={`flex items-center gap-4 border rounded-xl p-5 transition-colors ${active ? "border-[#A78BFA]/50 bg-[#A78BFA]/5" : "border-white/10 bg-[#111113]"}`}
                >
                  {done ? (
                    <CheckCircle2 className="w-6 h-6 text-[#A78BFA] shrink-0" />
                  ) : (
                    <stage.icon className={`w-6 h-6 shrink-0 ${active ? "text-[#A78BFA] animate-pulse" : "text-zinc-600"}`} />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${active || done ? "text-white" : "text-zinc-500"}`}>{stage.label}</p>
                    {active && stage.key === "identifying_and_pricing" && job?.total_cards_detected > 0 && (
                      <div className="mt-1.5 space-y-1.5">
                        <p className="text-xs text-zinc-500">{job.cards_processed || 0} of {job.total_cards_detected} cards priced</p>
                        <Progress
                          data-testid="processing-cards-progress"
                          value={Math.round(((job.cards_processed || 0) / job.total_cards_detected) * 100)}
                          className="bg-white/10 h-1.5 w-48"
                        />
                        {(job.cards_processed || 0) === 0 && (
                          <p className="text-[10px] text-zinc-600">Each card is identified &amp; priced from live sold data — expect roughly 30-60s per card, longer on busy sets.</p>
                        )}
                      </div>
                    )}
                    {active && stage.key === "detecting_cards" && (
                      <div className="mt-1.5 space-y-1.5">
                        <p className="text-xs text-zinc-500">
                          {job?.detect_progress != null
                            ? `${job.detect_progress}% of video analysed`
                            : "Scanning frames for each card placed..."}
                        </p>
                        {job?.detect_progress != null && (
                          <Progress
                            data-testid="processing-video-progress"
                            value={job.detect_progress}
                            className="bg-white/10 h-1.5 w-48"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {(!job || job.status === "processing" || job.status === "uploaded") && (
              <Button
                data-testid="upload-cancel-scan-button"
                variant="outline"
                onClick={cancelScan}
                className={`w-full rounded-full ${confirmCancel ? "border-red-500 text-red-400 bg-red-500/10 hover:bg-red-500/20 hover:text-red-400" : "border-red-500/30 text-red-400/80 hover:bg-red-500/10 hover:text-red-400"}`}
              >
                {confirmCancel ? "Really stop this scan?" : "Cancel scan"}
              </Button>
            )}
            {job?.status === "completed" && job?.credit_limit_reached && (
              <div data-testid="video-processing-credit-limit-notice" className="border border-[#F59E0B]/30 bg-[#F59E0B]/5 rounded-xl p-5 text-sm text-[#F59E0B]">
                You ran out of credits partway through — the remaining cards were saved as photos only.{" "}
                <Link to="/billing" className="underline underline-offset-2">Buy more credits</Link> then re-scan them from your binder.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
